import api from './axios';

// Module-level memo for categories (data, fetch time, in-flight promise)
const categoriesCache = { data: null, at: 0, inflight: null };

export const providersAPI = {
  // Get all providers (with optional filters) - only approved providers
  getProviders: async (params = {}) => {
    const response = await api.get('/providers', { params });
    return response.data;
  },

  // Get provider by ID
  getProvider: async (id) => {
    const response = await api.get(`/providers/${id}`);
    return response.data;
  },

  // Search providers
  searchProviders: async (query) => {
    const response = await api.get('/providers', { params: query });
    return response.data;
  },

  // Create provider profile (for provider registration)
  createProvider: async (data) => {
    const response = await api.post('/providers', data);
    return response.data;
  },

  // Get authenticated provider's profile
  getMyProfile: async () => {
    const response = await api.get('/providers/me/profile');
    return response.data;
  },

  // Update authenticated provider's profile
  updateMyProfile: async (data) => {
    const response = await api.patch('/providers/me/profile', data);
    return response.data;
  },

  // Admin: Get all providers (including unapproved)
  getAllProviders: async (params = {}) => {
    const response = await api.get('/providers/admin/providers', { params });
    return response.data;
  },

  // Admin: Approve provider
  approveProvider: async (providerId) => {
    const response = await api.post(`/verification/approve/${providerId}`);
    return response.data;
  },

  // Admin: Reject provider
  rejectProvider: async (providerId, reason) => {
    const response = await api.post(`/verification/reject/${providerId}`, { reason });
    return response.data;
  },

  // Get categories — memoized: they rarely change and nearly every page asks
  // for them, so one network trip per 10 minutes is plenty.
  getCategories: async () => {
    const now = Date.now();
    if (categoriesCache.data && now - categoriesCache.at < 10 * 60 * 1000) {
      return categoriesCache.data;
    }
    if (!categoriesCache.inflight) {
      categoriesCache.inflight = api.get('/categories')
        .then((response) => {
          categoriesCache.data = response.data;
          categoriesCache.at = Date.now();
          return response.data;
        })
        .finally(() => { categoriesCache.inflight = null; });
    }
    return categoriesCache.inflight;
  },

  // Admin: Create category
  createCategory: async (data) => {
    const response = await api.post('/categories', data);
    return response.data;
  },

  // Admin: Update category
  updateCategory: async (id, data) => {
    const response = await api.patch(`/categories/${id}`, data);
    return response.data;
  },

  // Get provider services (public - by provider ID)
  getProviderServices: async (providerId) => {
    const response = await api.get('/services', { params: { providerId } });
    return response.data;
  },

  // Get my services (provider only - uses JWT)
  getMyServices: async () => {
    const response = await api.get('/services/me/all');
    return response.data;
  },

  // Create service (provider only - uses JWT)
  createService: async (data) => {
    const response = await api.post('/services', data);
    return response.data;
  },

  // Update service
  updateService: async (serviceId, data) => {
    const response = await api.patch(`/services/${serviceId}`, data);
    return response.data;
  },

  // Delete service
  deleteService: async (serviceId) => {
    const response = await api.delete(`/services/${serviceId}`);
    return response.data;
  },

  // Get availability
  getAvailability: async (providerId) => {
    const response = await api.get(`/availability/providers/${providerId}`);
    return response.data;
  },

  // Save single day availability (creates or updates)
  saveAvailability: async (data) => {
    const response = await api.post('/availability', data);
    return response.data;
  },

  // Delete availability slot
  deleteAvailability: async (id) => {
    const response = await api.delete(`/availability/${id}`);
    return response.data;
  },

  // Get reviews
  getReviews: async (providerId, params = {}) => {
    const response = await api.get(`/reviews/providers/${providerId}`, { params });
    return response.data;
  },

  // Get all reviews by the authenticated user
  getMyReviews: async () => {
    const response = await api.get('/reviews/me');
    return response.data;
  },

  // Submit a review for a provider
  submitReview: async (providerId, data) => {
    const response = await api.post(`/reviews/providers/${providerId}`, data);
    return response.data;
  },

  // Respond to a review (provider only)
  respondToReview: async (reviewId, responseText) => {
    const response = await api.post(`/reviews/${reviewId}/respond`, { response: responseText });
    return response.data;
  },

  // Addons
  createAddon: async (serviceId, data) => {
    const response = await api.post(`/addons/services/${serviceId}`, data);
    return response.data;
  },

  deleteAddon: async (addonId) => {
    const response = await api.delete(`/addons/${addonId}`);
    return response.data;
  },

  // Photos
  getPhotos: async (providerId) => {
    const response = await api.get(`/photos/providers/${providerId}`);
    return response.data;
  },

  uploadPhoto: async (data) => {
    const response = await api.post('/photos', data);
    return response.data;
  },

  deletePhoto: async (photoId) => {
    const response = await api.delete(`/photos/${photoId}`);
    return response.data;
  },

  reorderPhotos: async (photoIds) => {
    const response = await api.post('/photos/reorder', { photoIds });
    return response.data;
  },
};
